import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useMainStore from "../../store/mainState";
import CashIn from "./cashIn";
import CashOut from "./cashout";
import { ReactComponent as BitcoinIcon } from "../../assets/icons/bitcoin.svg";

const PaymentMethods = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { isPaymentOpen, setIsPaymentOpen } = useMainStore();
    const [activeTab, setActiveTab] = useState<"deposit" | "withdraw">("deposit");
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>("");

    // Open modal when navigating to /payment
    useEffect(() => {
        setIsPaymentOpen(true);
        return () => setIsPaymentOpen(false);
    }, [setIsPaymentOpen]);

    const handleClose = () => {
        setIsPaymentOpen(false);
        navigate("/");
    };

    const handleBack = () => {
        if (selectedPaymentMethod) {
            setSelectedPaymentMethod("");
        } else {
            handleClose();
        }
    };

    if (!isPaymentOpen) return null;

    return (
        <div
            className="fixed inset-0 z-[200] flex items-center justify-center"
            onClick={handleClose}
        >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

            {/* Modal */}
            <div
                className="relative w-full max-w-md mx-4 rounded-2xl p-6 flex flex-col gap-5 items-center max-h-[90vh] overflow-y-auto"
                style={{
                    background: "rgba(16, 24, 40, 0.95)",
                    border: "1px solid rgba(77, 189, 255, 0.12)",
                    boxShadow: "0 24px 80px rgba(0, 0, 0, 0.6), 0 0 80px rgba(77, 189, 255, 0.06)",
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Top Bar: Back + Title + Close */}
                <div className="flex items-center w-full gap-3">
                    <button
                        onClick={handleBack}
                        className="flex items-center justify-center w-8 h-8 rounded-lg transition-all duration-200 hover:bg-white/[0.08] active:scale-95 flex-shrink-0"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="15 18 9 12 15 6"></polyline>
                        </svg>
                    </button>
                    <div className="flex-1 min-w-0">
                        <h1 className="text-base font-bold text-white capitalize truncate">
                            {selectedPaymentMethod
                                ? `${activeTab === "deposit" ? t('payment.deposit') : t('payment.withdraw')} - ${selectedPaymentMethod}`
                                : t('payment.title')}
                        </h1>
                        {!selectedPaymentMethod && (
                            <p className="text-white/35 text-[11px] mt-0.5">{t('payment.chooseTransact')}</p>
                        )}
                    </div>
                    <button
                        onClick={handleClose}
                        className="flex items-center justify-center w-8 h-8 rounded-lg transition-all duration-200 hover:bg-white/[0.08] active:scale-95 flex-shrink-0"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                    </button>
                </div>

                <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-white/8 to-transparent" />

                {/* Tab Switcher - always visible */}
                <div
                    className="rounded-xl p-1 flex gap-1 w-full"
                    style={{ background: "rgba(255, 255, 255, 0.05)" }}
                >
                    <button
                        className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-all duration-300 ${
                            activeTab === "deposit"
                                ? "bg-gradient-to-r from-[#4DBDFF] to-[#0086D3] text-white shadow-lg shadow-[#0086D3]/25"
                                : "text-white/40 hover:text-white/70"
                        }`}
                        onClick={() => { setActiveTab("deposit"); setSelectedPaymentMethod(selectedPaymentMethod || ""); }}
                    >
                        {t('payment.deposit')}
                    </button>
                    <button
                        className={`flex-1 py-2 rounded-lg text-xs font-semibold transition-all duration-300 ${
                            activeTab === "withdraw"
                                ? "bg-gradient-to-r from-[#4DBDFF] to-[#0086D3] text-white shadow-lg shadow-[#0086D3]/25"
                                : "text-white/40 hover:text-white/70"
                        }`}
                        onClick={() => { setActiveTab("withdraw"); setSelectedPaymentMethod(selectedPaymentMethod || ""); }}
                    >
                        {t('payment.withdraw')}
                    </button>
                </div>

                {selectedPaymentMethod ? (
                    activeTab === "deposit" ? (
                        <CashIn setSelectedPaymentMethod={setSelectedPaymentMethod} />
                    ) : (
                        <CashOut setSelectedPaymentMethod={setSelectedPaymentMethod} />
                    )
                ) : (
                    <>
                        {/* Payment Method Selection */}
                        <button
                            className="group relative w-full rounded-xl overflow-hidden transition-all duration-200 hover:bg-white/[0.04] active:scale-[0.99]"
                            onClick={() => setSelectedPaymentMethod("crypto")}
                            style={{
                                background: "rgba(255, 255, 255, 0.02)",
                                border: "1px solid rgba(255, 255, 255, 0.08)",
                            }}
                        >
                            <div className="flex items-center gap-4 px-4 py-3.5">
                                <div
                                    className="w-11 h-11 rounded-xl overflow-hidden flex items-center justify-center flex-shrink-0"
                                    style={{ background: "rgba(77, 189, 255, 0.08)" }}
                                >
                                    <BitcoinIcon className="w-6 h-6 group-hover:scale-110 transition-transform duration-300" />
                                </div>
                                <div className="flex-1 text-left">
                                    <p className="text-white text-sm font-medium">{t('payment.cryptocurrency')}</p>
                                    <p className="text-white/35 text-[11px] mt-0.5">{t('payment.cryptoDesc')}</p>
                                </div>
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="group-hover:stroke-[#4DBDFF] transition-colors duration-200">
                                    <polyline points="9 18 15 12 9 6"></polyline>
                                </svg>
                            </div>
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

export default PaymentMethods;
